import React, { useMemo } from 'react'

import {
  AppTheme,
  Box,
  Card,
  CardProps,
  Chip,
  Divider,
  Health,
  makeAppStyles,
  PaletteType,
  ProviderLogo,
  Typography,
  useAppTheme,
} from '@percept/mui'

import {
  OrgUnitProviderCategoryScoreTimeseriesDatum,
  ProviderCategoryScoreTimeseriesDatum,
  TimeseriesHealthDatum,
} from '@percept/hooks'

import {
  ChartData,
  dmyFormatter,
  Domain,
  financialYearFormatter,
  longMonthYearFormatter,
  quarterFormatter,
  rateFormatter,
  ResponsiveLine,
  weekFormatter,
} from '@percept/mui/charts'

import { get, last, mapValues, sortBy } from 'lodash-es'

import { healthTimeseriesToChartData, HEALTH_GRADIENT_ID, HealthDefs, getFullTimeseries, getTimeseriesDateRange } from './lib'

import { getInsightCategoryLabel, getInsightCategoryOrder, TimelineDatum } from '@percept/app-components'

import { DateType, InsightCategory, ReportProviderV2, TimeseriesGranularity } from '@percept/types'

import { TickFormatter } from '@visx/axis'


const healthDomain: Domain = [0, 1]

// Idempotent reference to avoid recomputations in placeholder charts
const EMPTY_CHART_DATA_REF: ChartData<TimelineDatum> = []

const timeseriesGranularityFormatters: Record<TimeseriesGranularity, TickFormatter<DateType>> = {
  FINANCIAL_YEAR: financialYearFormatter,
  CALENDAR_YEAR: longMonthYearFormatter,
  MONTH: longMonthYearFormatter,
  DAY: dmyFormatter,
  WEEK: weekFormatter,
  QUARTER: quarterFormatter,
}


const getCategoryOrder = (categories: string[]): string[] => (
  getInsightCategoryOrder(
    categories.filter( c => c !== 'OVERALL' ) as InsightCategory[]
  )
)


const themedBackgroundRectProps: Record<PaletteType, Partial<React.SVGAttributes<SVGRectElement>>> = {
  dark: {
    fill: '#FFF',
    fillOpacity: 0.05,
  },
  light: {
    fill: '#000',
    fillOpacity: 0.01,
  },
}

const healthFormatter: TickFormatter<any> = (d) => (
  d === null ? 'N / A' : rateFormatter(d)
)


const HealthTimeseries = ({
  chartData,
  appTheme,
  granularity,
}: {
  granularity: TimeseriesGranularity
  chartData: ChartData<TimelineDatum>
  appTheme: AppTheme
}): JSX.Element => {
  const healthDefs = <HealthDefs appTheme={appTheme} />
  const backgroundRectProps = themedBackgroundRectProps[appTheme.palette.type]
  const xTickFormatter = timeseriesGranularityFormatters[granularity]
  return (
    <ResponsiveLine
      animate={false}
      defs={healthDefs}
      data={chartData}
      domain={healthDomain}
      roundXDomain={false}
      roundYDomain={false}
      verticalMargin={0}
      axisLine={false}
      strokeOverride={`url(#${HEALTH_GRADIENT_ID})`}
      xTickFormatter={xTickFormatter}
      yTickFormatter={healthFormatter}
      backgroundElement={
        <rect width='100%' height='100%' {...backgroundRectProps} />
      }
      xScaleType='time' />
  )
}


const useHealthTimeseriesViewStyles = makeAppStyles( theme => ({
  container: {
    height: 28,
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  categoryContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  chip: {
    fontSize: 12,
    height: 20,
    padding: '0 1px',
  },
  categoryLabel: {
    fontSize: 12,
    fontWeight: theme.typography.fontWeightBold,
    marginRight: theme.spacing(2),
    minWidth: '5rem',
    textWrap: 'nowrap'
  },
  chartContainer: {
    display: 'flex',
    position: 'relative',
    flexGrow: 1,
    minWidth: 0,
    height: 20,
    marginLeft: theme.spacing(2),
  },
  chartUnavailablePlaceholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    fontWeight: theme.typography.fontWeightBold,
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  health: {
    fontSize: '1.15em',
    justifyContent: 'flex-end',
    minWidth: '3.5em',
  },
}))


const HealthTimeseriesView = ({
  label,
  chartData,
  granularity,
  appTheme,
  minLabelWidth,
  onClick,
}: {
  label: React.ReactNode
  chartData: ChartData<TimelineDatum>
  granularity: TimeseriesGranularity
  appTheme: AppTheme
  minLabelWidth?: string | number
  onClick?: () => void
}): JSX.Element => {
  const classes = useHealthTimeseriesViewStyles()
  const latestHealth = get(last(chartData), 'value', null)
  return (
    <div
      className={classes.container}>
      { onClick ? (
        <span
          className={classes.categoryLabel}
          style={{minWidth: minLabelWidth}}>
          <Chip
            classes={{
              root: classes.chip,
            }}
            variant='outlined'
            size='small'
            onClick={onClick}
            label={label} />
        </span>
      ) : (
        <Typography
          className={classes.categoryLabel}
          style={{minWidth: minLabelWidth}}>
          { label }
        </Typography>
      )}
      { latestHealth === null ? (
        <div className={classes.health} />
      ) : (
        <Health
          animate={false}
          className={classes.health}
          value={latestHealth} />
      )}
      <div className={classes.chartContainer}>
        { chartData.length > 1 ? (
          <HealthTimeseries
            chartData={chartData}
            granularity={granularity}
            appTheme={appTheme} />
        ) : (
          <>
            <HealthTimeseries
              chartData={EMPTY_CHART_DATA_REF}
              granularity={granularity}
              appTheme={appTheme} />
            <div className={classes.chartUnavailablePlaceholder}>
              N / A
            </div>
          </>
        )}
      </div>
    </div>
  )
}


const useHealthTimeseriesGroupStyles = makeAppStyles( theme => ({
  container: {
    height: 28,
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  labelContainer: {
    display: 'flex',
    textWrap: 'nowrap',
  },
  chartContainer: {
    display: 'flex',
    position: 'relative',
    height: 20,
    flexGrow: 1,
    minWidth: 0,
    marginLeft: theme.spacing(2),
  },
  chartUnavailablePlaceholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    fontWeight: theme.typography.fontWeightBold,
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  health: {
    fontSize: '1.5em',
    justifyContent: 'flex-end',
    paddingLeft: theme.spacing(2),
    marginLeft: 'auto',
  },
}))


type TimeseriesGroupDataset = {
  key: string
  data: ChartData<TimelineDatum>
  label: React.ReactNode
}

type OrgCategoryTimeseriesGroupDataset = TimeseriesGroupDataset & {
  org_unit_id: string
  category: string
}

type ProviderCategoryTimeseriesGroupDataset = TimeseriesGroupDataset & {
  provider: ReportProviderV2
  category: string
}

function HealthTimeseriesGroup<Dataset extends TimeseriesGroupDataset = TimeseriesGroupDataset>({
  aggregated,
  items,
  appTheme,
  granularity,
  minLabelWidth,
  onItemClick,
  ...props
}: {
  aggregated: Dataset
  items: Dataset[]
  appTheme: AppTheme
  granularity: TimeseriesGranularity
  minLabelWidth?: string | number
  onItemClick?: (d: Dataset) => void
} & CardProps): JSX.Element {
  const classes = useHealthTimeseriesGroupStyles()
  const aggregatedHealthValue = get(last(aggregated.data), 'value', null)
  return (
    <Card {...props}>
      <Box p={2} flexBasis='100%'>
        <Box className={classes.container} mb={2} display='flex' alignItems='center'>
          <div
            className={classes.labelContainer}
            style={{
              minWidth: minLabelWidth,
            }}>
            { aggregated.label }
          </div>
          { aggregatedHealthValue === null ? (
            <div className={classes.health} />
          ) : (
            <Health
              animate={false}
              className={classes.health}
              value={aggregatedHealthValue} />
          )}
          <div className={classes.chartContainer}>
            { aggregated.data.length > 1 ? (
              <HealthTimeseries
                chartData={aggregated.data}
                granularity={granularity}
                appTheme={appTheme} />
            ) : (
              <>
                <HealthTimeseries
                  chartData={EMPTY_CHART_DATA_REF}
                  granularity={granularity}
                  appTheme={appTheme} />
                <div className={classes.chartUnavailablePlaceholder}>
                  N / A
                </div>
              </>
            )}
          </div>
        </Box>
        <Divider />
        <Box mt={2}>
          { items.map( (item, i) => (
            <HealthTimeseriesView
              key={`health-items-${item.key}`}
              onClick={
                onItemClick ? ((): void => {
                  onItemClick(item)
                }) : undefined
              }
              chartData={item.data}
              appTheme={appTheme}
              granularity={granularity}
              minLabelWidth={minLabelWidth}
              label={item.label} />
          ))}
        </Box>
      </Box>
    </Card>
  )
}


const providerLabelWidths: Partial<Record<ReportProviderV2, string | number>> = {
  DV360: 116,
  GOOGLE_ADS: 75,
  FACEBOOK: 75,
  TIKTOK: 75,
  ADFORM: 75,
}

const clickableProviderLabelWidths: Partial<Record<ReportProviderV2, string | number>> = {
  DV360: 116,
  GOOGLE_ADS: 95,
  FACEBOOK: 95,
  TIKTOK: 95,
  ADFORM: 95,
}

export type ProviderCategoryScoresProps = ProviderCategoryScoreTimeseriesDatum & {
  granularity: TimeseriesGranularity
  onItemClick?: (d: ProviderCategoryTimeseriesGroupDataset) => void
} & CardProps

export const ProviderCategoryScores = ({
  provider,
  timeseriesByCategory,
  granularity,
  onItemClick,
  ...props
}: ProviderCategoryScoresProps): JSX.Element => {
  const appTheme = useAppTheme()
  const chartDataMap = useMemo(() => {
    const timeseriesDateRange = getTimeseriesDateRange(timeseriesByCategory.OVERALL)
    return mapValues(
      timeseriesByCategory,
      timeseries => getFullTimeseries({
        timeseries: healthTimeseriesToChartData({
          timeseries,
          appTheme,
        }),
        dateRange: timeseriesDateRange,
        granularity,
        datumDefaults: {
          color: appTheme.palette.neutral.main,
        },
      }),
    )
  }, [timeseriesByCategory, granularity, appTheme])
  const categoryOrder = getCategoryOrder(Object.keys(chartDataMap))
  const labelWidthMap = onItemClick ? clickableProviderLabelWidths : providerLabelWidths
  return (
    <HealthTimeseriesGroup
      aggregated={{
        provider,
        category: 'OVERALL',
        key: 'OVERALL',
        data: chartDataMap.OVERALL,
        label: <ProviderLogo size={1.5} provider={provider} />
      }}
      items={
        categoryOrder.map( category => ({
          key: category,
          provider,
          category,
          data: chartDataMap[category],
          label: getInsightCategoryLabel(category),
        }))
      }
      onItemClick={onItemClick}
      appTheme={appTheme}
      granularity={granularity}
      minLabelWidth={labelWidthMap[provider]}
      {...props} />
  )
}


const useCategoryScoreGroupStyles = makeAppStyles( theme => ({
  group: {
    display: 'flex',
    flexWrap: 'nowrap',
    [theme.breakpoints.down('lg')]: {
      flexWrap: 'wrap',
    },
  },
  card: ({ groupCount }: { groupCount: number }) => {
    const lgWidth = groupCount >= 6 ? 25 : 100 / groupCount 
    const mdWidth = groupCount >= 4 ? 100 / 3 : 100 / groupCount
    return {
      marginRight: theme.spacing(2),
      flexGrow: 1,
      minWidth: 0,
      '&:last-of-type': {
        marginRight: 0,
      },
      [theme.breakpoints.down('lg')]: {
        maxWidth: `calc(${lgWidth}% - ${theme.spacing(2)}px)`,
        flexBasis: `${lgWidth}%`,
        marginBottom: theme.spacing(2),
      },
      [theme.breakpoints.down('md')]: {
        maxWidth: `calc(${mdWidth}% - ${theme.spacing(2)}px)`,
        flexBasis: `${mdWidth}%`,
        marginBottom: theme.spacing(2),
      },
      [theme.breakpoints.down('sm')]: {
        maxWidth: `calc(50% - ${theme.spacing(2)}px)`,
        flexBasis: '50%',
        marginBottom: theme.spacing(2),
      },
      [theme.breakpoints.down('xs')]: {
        maxWidth: '100%',
        flexBasis: '100%',
        marginBottom: theme.spacing(2),
      },
    }
  },
  title: {
    fontSize: 13,
    fontWeight: theme.typography.fontWeightBold,
  }
}))


export type CategoryScoreGroupProps = {
  categoryScores: ProviderCategoryScoreTimeseriesDatum[]
  granularity: TimeseriesGranularity
  onItemClick?: (d: ProviderCategoryTimeseriesGroupDataset) => void
}

export const CategoryScoreGroup = ({
  categoryScores,
  granularity,
  onItemClick,
}: CategoryScoreGroupProps): JSX.Element => {
  const classes = useCategoryScoreGroupStyles({ groupCount: categoryScores.length })
  return (
    <div className={classes.group}>
      { categoryScores.map( providerCategoryScores => (
        <ProviderCategoryScores
          key={providerCategoryScores.provider}
          granularity={granularity}
          onItemClick={onItemClick}
          {...providerCategoryScores}
          classes={{
            root: classes.card,
          }} />
      ))}
    </div>
  )
}


export type OrgUnitCategoryScoreGroupProps = {
  aggregatedCategoryScores: Record<string, TimeseriesHealthDatum[]>
  orgUnitProviderCategoryScores: OrgUnitProviderCategoryScoreTimeseriesDatum[]
  granularity: TimeseriesGranularity
  onItemClick?: (orgCategoryDataset: OrgCategoryTimeseriesGroupDataset) => void
}

export const OrgUnitCategoryScoreGroup = ({
  orgUnitProviderCategoryScores,
  aggregatedCategoryScores,
  granularity,
  onItemClick,
}: OrgUnitCategoryScoreGroupProps): JSX.Element => {
  const appTheme = useAppTheme()

  const aggregatedChartDataMap = useMemo(() => {
    const overallDateRange = getTimeseriesDateRange(aggregatedCategoryScores.OVERALL)
    return mapValues(
      aggregatedCategoryScores,
      timeseries => getFullTimeseries({
        timeseries: healthTimeseriesToChartData({
          timeseries,
          appTheme,
        }),
        granularity,
        dateRange: overallDateRange,
        datumDefaults: {
          color: appTheme.palette.neutral.main,
        },
      })
    )
  }, [aggregatedCategoryScores, granularity, appTheme])

  const categoryMapping = useMemo(() => {
    const overallDateRange = getTimeseriesDateRange(aggregatedCategoryScores.OVERALL)
    const mapping = orgUnitProviderCategoryScores.reduce( (acc, d) => {
      Object.entries(d.timeseriesByCategory).forEach( ([category, data ]) => {
        if( category === 'OVERALL' ){
          return acc
        }
        const orgCategoryTimeseries = healthTimeseriesToChartData({ timeseries: data, appTheme })
        const fullTimeseries = getFullTimeseries({
          timeseries: orgCategoryTimeseries,
          dateRange: overallDateRange,
          granularity,
          datumDefaults: {
            color: appTheme.palette.neutral.main,
          },
        })
        acc[category] = acc[category] || {}
        acc[category][d.org_unit_id] = {
          key: `${category}-${d.org_unit_id}`,
          org_unit_id: d.org_unit_id,
          category,
          data: fullTimeseries,
          label: d.org_unit_name,
        }
      })
      return acc
    }, {} as Record<string, Record<string, OrgCategoryTimeseriesGroupDataset>>)

    return mapValues(
      mapping,
      categoryMapping => sortBy(Object.values(categoryMapping), 'label')
    )
  }, [orgUnitProviderCategoryScores, aggregatedCategoryScores, granularity, appTheme])

  const categoryOrder = getCategoryOrder(Object.keys(categoryMapping))

  const classes = useCategoryScoreGroupStyles({ groupCount: categoryOrder.length })

  return (
    <div className={classes.group}>
      { categoryOrder.map( category => {
        return (
          <HealthTimeseriesGroup
            key={category}
            aggregated={{
              key: category,
              org_unit_id: '',
              category,
              data: aggregatedChartDataMap[category],
              label: (
                <Typography className={classes.title}>
                  {getInsightCategoryLabel(category)}
                </Typography>
              ),
            }}
            items={categoryMapping[category]}
            onItemClick={onItemClick}
            granularity={granularity}
            appTheme={appTheme}
            minLabelWidth='4.75rem'
            classes={{
              root: classes.card,
            }} />
        )
      })}
    </div>
  )
}
